import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts';
import InfoTooltip from '../common/InfoTooltip';
import { useCurrency } from '../../hooks/useCurrency';

// Lightweight shadcn-like card wrappers using existing canon/tailwind tokens
const Card = ({ className = '', children }) => (
  <div className={`rounded-md border border-succession-border bg-succession-bg-primary ${className}`}>{children}</div>
);
const CardHeader = ({ children }) => (
  <div className="px-4 pt-4">{children}</div>
);
const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-xl font-semibold ${className}`}>{children}</h3>
);
const CardContent = ({ children }) => (
  <div className="p-4">{children}</div>
);

const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 8} startAngle={startAngle} endAngle={endAngle} fill={fill} />
    </g>
  );
};

// Resolve a CSS variable to a concrete color string from the active tenant theme
const resolveVar = (varName) => {
  try {
    if (typeof window === 'undefined' || !window.document) return `var(${varName})`;
    const val = getComputedStyle(document.documentElement).getPropertyValue(varName);
    return (val && val.trim()) ? val.trim() : `var(${varName})`;
  } catch (e) {
    return `var(${varName})`;
  }
};

// Map canonical colors from UI-canon/theming when data color is not provided
const getCanonColor = (name) => {
  const n = (name || '').toLowerCase();
  // business lines
  if (n.includes('wealth')) return resolveVar('--succession-success');
  if (n.includes('life')) return resolveVar('--succession-secondary');
  if (n.includes('pc') || n.includes('p&c') || n.includes('p\u0026c')) return resolveVar('--succession-warning');
  if (n.includes('group')) return resolveVar('--succession-error');
  // methods
  if (n.includes('dcf')) return resolveVar('--succession-success');
  if (n.includes('mor')) return resolveVar('--succession-secondary');
  if (n.includes('ebitda')) return resolveVar('--succession-warning');
  if (n.includes('asset')) return resolveVar('--succession-accent');
  return resolveVar('--succession-primary');
};

// Format line label for display
// Preserve all-uppercase names (methodology abbreviations like MOR, DCF, EBITDA)
// Title-case lowercase/mixed names (business lines like wealth -> Wealth)
const formatLineLabel = (name) => {
  if (!name) return '';
  const s = String(name);
  // If the name is already all uppercase (e.g. MOR, DCF, EBITDA), keep it as-is
  if (s === s.toUpperCase() && s.length > 1) return s;
  const n = s.toLowerCase();
  if (n === 'pc') return 'P&C';
  return n.charAt(0).toUpperCase() + n.slice(1);
};

export default function ValuationDonutChart({ title, centerLabel, centerValue, data, className = '', tooltips, onLegendClick }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const { symbol: currencySym } = useCurrency();
  const total = (data || []).reduce((sum, item) => sum + (Number(item.value) || 0), 0);

  const formatValue = (value) => {
    const v = Number(value || 0);
    const sym = currencySym;
    if (v >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000) return `${sym}${(v / 1_000).toFixed(1)}K`;
    return `${sym}${v.toFixed(0)}`;
  };

  const formatPercentage = (value) => {
    const v = Number(value || 0);
    return total > 0 ? `${((v / total) * 100).toFixed(1)}%` : '0.0%';
  };

  return (
    <Card className={`border-border ${className}`}>
      <CardHeader>
        <CardTitle className="text-xl font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div className="relative">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  onMouseEnter={(_, index) => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  activeIndex={activeIndex ?? undefined}
                  activeShape={renderActiveShape}
                >
                  {(data || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || getCanonColor(entry.name)} className="cursor-pointer transition-all duration-300" />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-sm text-succession-text-secondary font-medium mb-1">{centerLabel}</div>
              <div className="text-2xl font-bold text-succession-text-primary">{centerValue}</div>
            </div>
          </div>

          <div className="space-y-3">
            {(data || []).map((item, index) => {
              const key = (item.key || item.name || '').toLowerCase();
              const tooltip = tooltips && tooltips[key];
              const isClickable = Boolean(onLegendClick);
              return (
                <motion.div
                  key={item.name || index}
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  onClick={isClickable ? () => onLegendClick(key) : undefined}
                  className={`flex items-center justify-between p-3 rounded-lg border border-succession-border transition-all duration-300 cursor-pointer ${
                    activeIndex === index ? 'bg-succession-bg-tertiary shadow-md' : 'hover:bg-succession-bg-tertiary/60'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 shadow-sm" style={{ backgroundColor: item.color || getCanonColor(item.name) }} />
                    <span className="text-sm font-medium text-succession-text-primary">{formatLineLabel(item.name)}</span>
                    {tooltip && (
                      <span onClick={(e) => e.stopPropagation()}>
                        <InfoTooltip content={tooltip} pulse={false} side="top" />
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-base font-bold text-succession-text-primary">{formatValue(item.value)}</span>
                      <span className="text-xs text-succession-text-secondary font-medium">{formatPercentage(item.value)}</span>
                    </div>
                    {isClickable && (
                      <svg className="w-4 h-4 text-succession-text-secondary flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-succession-border">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-succession-text-secondary">Total</span>
            <span className="text-xl font-bold text-succession-text-primary">{formatValue(total)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


